import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { createAmpPlan } from "@/lib/amp/createPlan";

import { useAuth } from "@/context/auth";

export interface AmpPlan {
  id: string;
  project_id: string;
  folder_id: string | null;
  name: string;
  status: string;
  version: number;
  revision_note: string | null;
  revised_at: string | null;
  created_at: string;
  updated_at: string;
  handover_at?: string | null;
  handover_to?: string | null;
}

export function useAmpPlans(projectId: string, folderId?: string | null) {
  const { user } = useAuth();
  const [plans, setPlans] = useState<AmpPlan[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!projectId) return;
    let query = supabase
      .from("work_environment_plans")
      .select("id, project_id, folder_id, name, status, version, revision_note, revised_at, created_at, updated_at, handover_at, handover_to")
      .eq("project_id", projectId)
      .order("created_at", { ascending: true });
    if (folderId) query = query.eq("folder_id", folderId);

    const { data, error } = await query;
    if (error) {
      console.error("Could not load AMP plans", error);
    } else {
      setPlans((data || []) as AmpPlan[]);
    }
    setLoading(false);
  }, [projectId, folderId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const createPlan = useCallback(
    async (name: string, targetFolderId?: string | null): Promise<string | null> => {
      const { planId, sectionsError } = await createAmpPlan({
        projectId,
        folderId: targetFolderId ?? folderId ?? null,
        name,
        userId: user?.id ?? null,
      });

      if (!planId) {
        toast.error("Kunde inte skapa arbetsmiljöplanen");
        return null;
      }

      if (sectionsError) {
        toast.error("Kunde inte skapa AMP-avsnitten");
      } else {
        toast.success("Arbetsmiljöplan skapad och förifylld från Bas-processen");
      }

      await load();
      return planId;
    },
    [projectId, folderId, user?.id, load]
  );


  const deletePlan = useCallback(
    async (planId: string) => {
      const { error } = await supabase.from("work_environment_plans").delete().eq("id", planId);
      if (error) toast.error("Kunde inte ta bort arbetsmiljöplanen");
      else toast.success("Arbetsmiljöplanen togs bort");
      await load();
    },
    [load]
  );

  return { plans, loading, refresh: load, createPlan, deletePlan };
}
